import { useMemo } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloClientOptions
} from '@apollo/client';
import merge from 'deepmerge';
import getLink, { GetLinkOptions } from '@leuven2030/framework/apollo/getLink';
import isEqual from 'lodash/isEqual';
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<any> = null;

export type InitializeApolloOptions = {
  initialState?: any;
  possibleTypes?: any;
  linkOptions?: GetLinkOptions;
};

export function initializeApollo(options: InitializeApolloOptions = {}) {
  const { initialState, possibleTypes, linkOptions } = options;
  const _apolloClient = apolloClient ?? createApolloClient();
  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        )
      ]
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;

  function createApolloClient() {
    const options: ApolloClientOptions<any> = {
      ssrMode: typeof window === 'undefined',
      link: getLink(linkOptions),
      cache: new InMemoryCache({
        possibleTypes,
        typePolicies: {
          Document: {
            keyFields: ['_meta', ['uid', 'type']]
          }
        }
      })
    };

    return new ApolloClient(options);
  }
}

export function addApolloState(client, pageProps) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps = {}) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo({ initialState: state }), [
    state
  ]);
  return store;
}
